import React, { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Scale, Mail, Lock, AlertCircle, Loader2 } from 'lucide-react';
import { useAutenticacao } from '../contextos/useAutenticacao';

/**
 * PaginaLogin
 *
 * CONTEXTO DE NEGÓCIO:
 * Página de login usada para autenticar usuários na aplicação.
 * Implementa validações básicas no frontend e integra com o contexto de autenticação.
 */
export function PaginaLogin(): JSX.Element {
  const navigate = useNavigate();
  const { login, estaCarregando, erro } = useAutenticacao();

  const [email, setEmail] = useState<string>('');
  const [senha, setSenha] = useState<string>('');
  const [mostrarErroLocal, setMostrarErroLocal] = useState<string | null>(null);

  function validarFormulario() {
    if (!email) return 'Email é obrigatório.';
    // simples validação de formato
    const re = /^\S+@\S+\.\S+$/;
    if (!re.test(email)) return 'Formato de email inválido.';
    if (!senha) return 'Senha é obrigatória.';
    if (senha.length < 8) return 'A senha deve ter ao menos 8 caracteres.';
    return null;
  }

  async function aoSubmeter(event: FormEvent) {
    event.preventDefault();
    setMostrarErroLocal(null);
    const valido = validarFormulario();
    if (valido) {
      setMostrarErroLocal(valido);
      return;
    }

    try {
      await login(email, senha);
      // redireciona para a página inicial após login bem-sucedido
      navigate('/');
    } catch (err) {
      // o contexto já popula `erro`; aqui apenas garantimos foco e feedback local
      setMostrarErroLocal((err as Error)?.message || 'Erro ao realizar login');
    }
  }

  function aoDigitar() {
    // ao digitar, limpa erros locais para UX
    if (mostrarErroLocal) setMostrarErroLocal(null);
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-4">
      <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-lg">
        <div className="mb-6 text-center">
          <Scale className="mx-auto mb-4 size-12 text-primary-600" />
          <h1 className="text-2xl font-bold text-gray-900">Workforce Jurídico</h1>
          <p className="mt-2 text-gray-600">Entre na sua conta</p>
        </div>

        { (mostrarErroLocal || erro) && (
          <div className="mb-4 flex items-start gap-2 rounded-md border border-red-100 bg-red-50 p-3">
            <AlertCircle className="mt-0.5 size-5 text-red-600" aria-hidden />
            <div className="text-sm text-red-700">
              {mostrarErroLocal || erro}
            </div>
          </div>
        ) }

        <form onSubmit={aoSubmeter} aria-label="Formulário de login">
          <label className="mb-3 block">
            <span className="text-sm font-medium text-gray-700">Email</span>
            <div className="relative mt-1 rounded-md shadow-sm">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Mail className="size-4 text-gray-400" />
              </div>
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); aoDigitar(); }}
                required
                aria-required
                aria-label="Email"
                className="block w-full rounded-md border py-2 pl-10 pr-3 focus:outline-none focus:ring-2 focus:ring-primary-600"
                placeholder="seu@exemplo.com"
              />
            </div>
          </label>

          <label className="mb-3 block">
            <span className="text-sm font-medium text-gray-700">Senha</span>
            <div className="relative mt-1 rounded-md shadow-sm">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Lock className="size-4 text-gray-400" />
              </div>
              <input
                type="password"
                name="senha"
                value={senha}
                onChange={(e) => { setSenha(e.target.value); aoDigitar(); }}
                required
                aria-required
                aria-label="Senha"
                minLength={8}
                className="block w-full rounded-md border py-2 pl-10 pr-3 focus:outline-none focus:ring-2 focus:ring-primary-600"
                placeholder="********"
              />
            </div>
          </label>

          <div className="mb-4 flex items-center justify-between">
            <label className="inline-flex items-center text-sm text-gray-600">
              <input type="checkbox" className="mr-2" aria-label="Lembrar-me" /> Lembrar-me
            </label>
            <Link to="/recuperar-senha" className="text-sm text-primary-600 underline hover:text-primary-700">
              Esqueceu a senha?
            </Link>
          </div>

          <div>
            <button
              type="submit"
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-600 px-4 py-2 font-medium text-white hover:bg-primary-700 disabled:opacity-60"
              disabled={estaCarregando}
            >
              {estaCarregando ? <Loader2 className="size-4 animate-spin" /> : 'Entrar'}
            </button>
          </div>

          <p className="mt-4 text-center text-sm text-gray-600">
            Não tem conta? <Link to="/cadastro" className="text-primary-600 underline">Cadastre-se</Link>
          </p>
        </form>
      </div>
    </div>
  );
}

export default PaginaLogin;
